import axios from 'axios';
import configs from './Constants';

const dbPromise = new Promise((resolve, reject) => {
  try {
    if (!window.indexedDB) {
      console.warn("IndexedDB is not supported in this environment (WebView/Browser). Caching will be disabled.");
      return reject(new Error("IndexedDB not supported"));
    }
    const request = window.indexedDB.open('api-cache-db', 1);
    request.onupgradeneeded = (event) => {
      event.target.result.createObjectStore('cache');
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  } catch (err) {
    console.warn("Failed to initialize IndexedDB:", err);
    reject(err);
  }
});

const idbSet = async (key, val) => {
  const db = await dbPromise;
  return new Promise((resolve, reject) => {
    const tx = db.transaction('cache', 'readwrite');
    tx.objectStore('cache').put(val, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

const idbGet = async (key) => {
  const db = await dbPromise;
  return new Promise((resolve, reject) => {
    const tx = db.transaction('cache', 'readonly');
    const req = tx.objectStore('cache').get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

const idbKeys = async () => {
  const db = await dbPromise;
  return new Promise((resolve, reject) => {
    const tx = db.transaction('cache', 'readonly');
    const req = tx.objectStore('cache').getAllKeys();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

const idbRemove = async (key) => {
  const db = await dbPromise;
  return new Promise((resolve, reject) => {
    const tx = db.transaction('cache', 'readwrite');
    tx.objectStore('cache').delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

/**
 * Prefetch cacheable APIs to warm the cache.
 * On first launch (cache empty), fetches all endpoints.
 * When force=true, clears existing cache and re-fetches latest data.
 * @param {string} baseURL - The API base URL
 * @param {string} merchantCode - The merchant code
 * @param {string} userId - The user ID (for settings endpoint)
 * @param {string} token - The auth token
 * @param {boolean} force - If true, clear cache and re-fetch regardless
 */
export const prefetchCacheableApis = async (baseURL, merchantCode, userId, token, force = false) => {
  try {
    const keys = await idbKeys();
    const hasCachedData = keys.some(key => key.startsWith('cache_'));
    if (hasCachedData && !force) {
      console.log('[Cache Prefetch] Cache already populated, skipping prefetch.');
      return;
    }

    if (force && hasCachedData) {
      console.log('[Cache Prefetch] Force sync - clearing stale cache...');
      const deletePromises = keys
        .filter(key => key.startsWith('cache_'))
        .map(key => idbRemove(key));
      await Promise.all(deletePromises);
    }

    console.log('[Cache Prefetch] Fetching latest data...');

    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const endpoints = [
      `${baseURL}/api/categories?merchantCode=${merchantCode}`,
      `${baseURL}/api/products?merchantCode=${merchantCode}`,
      `${baseURL}/api/settings/${userId}`,
      `${baseURL}/api/offers?merchantCode=${merchantCode}`,
      `${baseURL}/api/promos?merchantCode=${merchantCode}`,
      `${baseURL}/api/varieties?merchantCode=${merchantCode}`,
      `${baseURL}/api/tables?merchantCode=${merchantCode}`,
    ];

    const results = await Promise.allSettled(
      endpoints.map(url =>
        axios.get(url, { headers }).then(async (response) => {
          if (response && response.status === 200) {
            const cacheKey = `cache_${url}`;
            const serializableResponse = {
              data: response.data,
              status: response.status,
              statusText: response.statusText,
              headers: response.headers,
            };
            await idbSet(cacheKey, JSON.stringify(serializableResponse));
            console.log(`[Cache Prefetch] Cached: ${url}`);
          }
          return response;
        })
      )
    );

    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;
    console.log(`[Cache Prefetch] Done. ${succeeded} succeeded, ${failed} failed.`);
    localStorage.setItem('lastSyncTime', new Date().getTime().toString());
  } catch (err) {
    console.warn('[Cache Prefetch] Error during prefetch:', err);
  }
};

export const syncLocalCache = async (baseURL, merchantCode, userId) => {
  const lastSyncTime = localStorage.getItem('lastSyncTime') || 0;
  try {
    const response = await axios.get(`${baseURL}/api/sync?merchantCode=${merchantCode}&lastSync=${lastSyncTime}`);
    if (response && response.data) {
      const data = response.data;
      if (data.products && data.products.length) await updateLocalCache(baseURL, merchantCode, userId, 'products', data.products);
      if (data.categories && data.categories.length) await updateLocalCache(baseURL, merchantCode, userId, 'categories', data.categories);
      if (data.settings && data.settings.length) await updateLocalCache(baseURL, merchantCode, userId, 'settings', data.settings);
      if (data.offers && data.offers.length) await updateLocalCache(baseURL, merchantCode, userId, 'offers', data.offers);
      if (data.promos && data.promos.length) await updateLocalCache(baseURL, merchantCode, userId, 'promos', data.promos);
      if (data.varieties && data.varieties.length) await updateLocalCache(baseURL, merchantCode, userId, 'varieties', data.varieties);
      if (data.tables && data.tables.length) await updateLocalCache(baseURL, merchantCode, userId, 'tables', data.tables);
      
      localStorage.setItem('lastSyncTime', new Date().getTime().toString());
    }
  } catch(err) {
    console.error("Sync error:", err);
  }
};

export const deleteFromLocalCache = async (deletedId) => {
   try {
       const keys = await idbKeys();
       let isUpdated = false;
       for (const key of keys) {
           if (key.startsWith('cache_')) {
               const cachedDataStr = await idbGet(key);
               if (cachedDataStr) {
                   const cachedResponse = JSON.parse(cachedDataStr);
                   if (cachedResponse && cachedResponse.data) {
                       let data = cachedResponse.data;
                       if (Array.isArray(data)) {
                           const initialLength = data.length;
                           data = data.filter(item => item._id !== deletedId && item.id !== deletedId);
                           if (data.length !== initialLength) {
                               cachedResponse.data = data;
                               await idbSet(key, JSON.stringify(cachedResponse));
                               isUpdated = true;
                           }
                       } else if (typeof data === 'object' && data !== null) {
                           if ((data._id === deletedId) || (data.id === deletedId)) {
                               cachedResponse.data = null;
                               await idbSet(key, JSON.stringify(cachedResponse));
                               isUpdated = true;
                           }
                       }
                   }
               }
           }
       }
       if (isUpdated) {
          window.dispatchEvent(new Event('local_cache_updated'));
       }
   } catch(err) {
       console.error("Failed to delete from cache", err);
   }
};


export const updateLocalCache = async (baseURL, merchantCode, userId, modelType, updatedRecords) => {
  if (!updatedRecords) return;
  const records = Array.isArray(updatedRecords) ? updatedRecords : [updatedRecords];
  if (records.length === 0) return;

  try {
    let url = "";
    if (modelType === 'products') url = `${baseURL}/api/products?merchantCode=${merchantCode}`;
    else if (modelType === 'categories') url = `${baseURL}/api/categories?merchantCode=${merchantCode}`;
    else if (modelType === 'settings') url = `${baseURL}/api/settings/${userId}`;
    else if (modelType === 'offers') url = `${baseURL}/api/offers?merchantCode=${merchantCode}`;
    else if (modelType === 'promos') url = `${baseURL}/api/promos?merchantCode=${merchantCode}`;
    else if (modelType === 'varieties') url = `${baseURL}/api/varieties?merchantCode=${merchantCode}`;
    else if (modelType === 'tables') url = `${baseURL}/api/tables?merchantCode=${merchantCode}`;
    
    if (!url) return;
    
    const cacheKey = `cache_${url}`;
    const cachedStr = await idbGet(cacheKey);
    if (cachedStr) {
      let cachedResponse = JSON.parse(cachedStr);
      let data = cachedResponse.data;
      if (Array.isArray(data)) {
        let isUpdated = false;
        records.forEach(record => {
          const recordId = record._id || record.id;
          if (!recordId) return;
          
          if (record.isDeleted) {
            const initialLength = data.length;
            data = data.filter(item => item._id !== recordId && item.id !== recordId);
            if (data.length !== initialLength) {
              isUpdated = true;
            }
          } else {
            const idx = data.findIndex(item => (item._id === recordId) || (item.id === recordId));
            if (idx !== -1) {
              data[idx] = { ...data[idx], ...record };
              isUpdated = true;
            } else {
               data.push(record);
               isUpdated = true;
            }
          }
        });
        if (isUpdated) {
          cachedResponse.data = data;
          await idbSet(cacheKey, JSON.stringify(cachedResponse));
          console.log(`[Cache Update] Updated local cache for ${modelType}`);
          // Dispatch event so components can refetch or update state if needed
          window.dispatchEvent(new CustomEvent('local_cache_updated', { detail: { modelType } }));
        }
      } else if (typeof data === 'object' && data !== null && modelType === 'settings') {
         // Settings might be an object instead of array depending on API, handle if needed
         let isUpdated = false;
         records.forEach(record => {
            const recordId = record._id || record.id;
            const dataId = data._id || data.id;
            if (recordId && dataId && recordId === dataId) {
               if (record.isDeleted) {
                 cachedResponse.data = null;
                 isUpdated = true;
               } else {
                 cachedResponse.data = { ...data, ...record };
                 isUpdated = true;
               }
            }
         });
         if (isUpdated) {
            await idbSet(cacheKey, JSON.stringify(cachedResponse));
            console.log(`[Cache Update] Updated local cache for ${modelType}`);
            window.dispatchEvent(new CustomEvent('local_cache_updated', { detail: { modelType } }));
         }
      }
    }
  } catch (err) {
    console.warn('[Cache Update] Error updating local cache:', err);
  }
};

export const clearApiCache = async () => {
  try {
    const keys = await idbKeys();
    const deletePromises = keys
      .filter(key => key.startsWith('cache_'))
      .map(key => idbRemove(key));
    await Promise.all(deletePromises);
  } catch (err) {
    console.warn("Failed to clear IndexedDB cache", err);
  }
};

/**
 * Handle session expiry by clearing auth data and redirecting to login.
 * Uses a flag to prevent multiple simultaneous redirects.
 */
let isRedirectingToLogin = false;
const handleSessionExpiry = () => {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;

  console.warn('[Session Expired] Token is invalid or expired. Redirecting to login...');

  localStorage.removeItem('token');
  localStorage.removeItem('userData');
  localStorage.removeItem('selectedBranchId');
  localStorage.removeItem('merchantData');
  localStorage.removeItem('branch-merchants');

  const isMember = localStorage.getItem('isMember');
  if (isMember) {
    localStorage.removeItem('merchantData');
    localStorage.removeItem('isMember');
    window.location.href = `${window.location.origin}/?${window.location.href.split('?')[1]}&logout=true`;
  } else if (configs && configs.authUrl) {
    window.location.href = configs.authUrl + '/account/login?redirect=' + window.location.origin + '&logout=true';
  } else {
    window.location.href = window.location.origin;
  }
};

/**
 * URLs that should never trigger an automatic session-expiry redirect.
 * These are login / auth-validation endpoints where a 401 is an expected
 * "wrong credentials" response, not a "session expired" signal.
 */
const SESSION_EXPIRY_EXCLUDE_URLS = [
  '/user/validate-member-names-pin',
  '/user/validate-token',
  '/members/open',
  '/merchants/',
];

const shouldSkipSessionExpiry = (url) => {
  if (!url) return false;
  return SESSION_EXPIRY_EXCLUDE_URLS.some((excluded) => url.includes(excluded));
};

export const setupCacheInterceptor = () => {
  // Global 401 interceptor — catches expired/invalid tokens on ANY API call
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const requestUrl = error.config && error.config.url;
      const hasAuthHeader = error.config && error.config.headers && error.config.headers.Authorization;
      if (
        error.response &&
        error.response.status === 401 &&
        hasAuthHeader && // Only expire if we were actually trying to authenticate this request
        !shouldSkipSessionExpiry(requestUrl) &&
        localStorage.getItem('token') // only expire if we actually have a session
      ) {
        handleSessionExpiry();
      }
      return Promise.reject(error);
    }
  );

  const originalGet = axios.get;

  const shouldCache = (url) => {
    if (!url) return false;
    // Cache specific endpoints: products, categories, settings, offers
    if (url.includes('/api/products') || url.includes('/api/categories') || url.includes('/api/settings') || url.includes('/api/offers') || url.includes('/api/promos') || url.includes('/api/varieties') || url.includes('/api/tables')) {
      return true;
    }
    return false;
  };

  const invalidateCache = async (url) => {
    if (!url) return;
    try {
      const keys = await idbKeys();
      if (url.includes('/api/products')) {
        keys.forEach(key => {
          if (key.startsWith('cache_') && key.includes('/api/products')) idbRemove(key);
        });
      }
      if (url.includes('/api/categories')) {
        keys.forEach(key => {
          if (key.startsWith('cache_') && key.includes('/api/categories')) idbRemove(key);
        });
      }
      if (url.includes('/api/settings') || url.includes('/api/configs')) {
        keys.forEach(key => {
          if (key.startsWith('cache_') && (key.includes('/api/settings') || key.includes('/api/configs'))) idbRemove(key);
        });
      }
      if (url.includes('/api/offers')) {
        keys.forEach(key => {
          if (key.startsWith('cache_') && key.includes('/api/offers')) idbRemove(key);
        });
      }
      if (url.includes('/api/promos')) {
        keys.forEach(key => {
          if (key.startsWith('cache_') && key.includes('/api/promos')) idbRemove(key);
        });
      }
      if (url.includes('/api/varieties')) {
        keys.forEach(key => {
          if (key.startsWith('cache_') && key.includes('/api/varieties')) idbRemove(key);
        });
      }
      if (url.includes('/api/tables')) {
        keys.forEach(key => {
          if (key.startsWith('cache_') && key.includes('/api/tables')) idbRemove(key);
        });
      }
    } catch (err) {
      console.warn("Failed to clear IndexedDB cache", err);
    }
  };

  axios.get = async function (url, config) {
    if (shouldCache(url)) {
      const cacheKey = `cache_${url}`;
      try {
        const cachedStr = await idbGet(cacheKey);
        if (cachedStr) {
          console.log(`[Cache Hit] ${url}`);
          const parsedData = JSON.parse(cachedStr);
          // Return a resolved promise matching axios response signature
          return Promise.resolve(parsedData);
        }
      } catch (err) {
        console.warn("Error reading from cache", err);
      }

      console.log(`[Cache Miss] ${url}`);
      try {
        const response = await originalGet.apply(axios, [url, config]);
        // Only cache successful GET requests
        if (response && response.status === 200) {
          const serializableResponse = {
            data: response.data,
            status: response.status,
            statusText: response.statusText,
            headers: response.headers
          };
          try {
             await idbSet(cacheKey, JSON.stringify(serializableResponse));
          } catch(e) {
             console.warn("Error setting cache", e);
          }
        }
        return response;
      } catch (error) {
        return Promise.reject(error);
      }
    }

    return originalGet.apply(axios, [url, config]);
  };

  // Override mutation methods to invalidate cache
  const originalPost = axios.post;
  axios.post = async function (url, data, config) {
    invalidateCache(url);
    return originalPost.apply(axios, [url, data, config]);
  };

  const originalPut = axios.put;
  axios.put = async function (url, data, config) {
    invalidateCache(url);
    return originalPut.apply(axios, [url, data, config]);
  };

  const originalPatch = axios.patch;
  axios.patch = async function (url, data, config) {
    invalidateCache(url);
    return originalPatch.apply(axios, [url, data, config]);
  };

  const originalDelete = axios.delete;
  axios.delete = async function (url, config) {
    invalidateCache(url);
    return originalDelete.apply(axios, [url, config]);
  };
};
